import React, {useState} from 'react';
import axios from "axios";
import {useAcceptJs} from "react-acceptjs";
import {Alert, Button, Card, Col, notification, Row, Statistic} from "antd";
import {Formik} from "formik";
import {Checkbox, Form, Input, SubmitButton} from "formik-antd";
import EntryPaymentDisclosure from "../components/EntryPaymentDisclosure";
import { useRealmApp } from "../utils/RealmApp";
import { createEntry, updateEntry } from "../services/entries";
import {createRider, getRiderByName} from "../services/riders";

function normalizeExpiration(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  if (digits.length === 2 && !raw.includes("/")) {
    return `${digits}/`;
  }
  return raw.length <= 5 ? raw : raw.slice(0, 5);
}

function expirationFieldOnChange(setFieldValue) {
  return (e) => {
    setFieldValue("expiration", normalizeExpiration(e.target.value));
  };
}

const PRICE = 100;
const CC_FEE = 0.04;
const amountDue = PRICE * (1 + CC_FEE);
// Authorize.Net returns two different codes. `transactionResponseCode` is the
// authoritative transaction status (1 = Approved, 2 = Declined, 3 = Error,
// 4 = Held for Review). `messageCode` is only a reason code and MUST NOT be used
// to decide approval — a decline reports messageCode "4", which previously
// collided with the old whitelist and let declined payments through as paid.
const approvedResponseCodes = ["1", "4"]; // Approved or Held for Review
// Fallback for API responses that predate `transactionResponseCode`. These are
// transaction MESSAGE codes for genuine approvals/holds only; a decline never
// carries these, so it cannot slip through.
const approvedMessageCodes = ["1", "252"];

function isPaymentApproved(paymentData) {
  if (!paymentData) return false;
  const txnCode =
    paymentData.transactionResponseCode != null
      ? String(paymentData.transactionResponseCode)
      : null;
  return txnCode !== null
    ? approvedResponseCodes.includes(txnCode)
    : approvedMessageCodes.includes(paymentData.messageCode);
}

const authData = {
  apiLoginID: process.env.REACT_APP_AUTHORIZENET_LOGINID,
  clientKey: process.env.REACT_APP_AUTHORIZENET_CLIENTKEY,
};

const EntryPaymentForm = ({horseName, foalObject, formData, riderObject, riderInputName, completeFormSubmission, eventObject, existingEntry}) => {
  const app = useRealmApp();
  const [api, contextHolder] = notification.useNotification();
  const [createdEntryId, setCreatedEntryId] = useState(null);
  const [createdRiderId, setCreatedRiderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openNotificationWithIcon = (type, title, description, placement) => {
    api[type]({
      message: title,
      description: description,
      placement,
    });
  };

  function cardDigits(value) {
    return String(value || "").replace(/\D/g, "");
  }

  function ValidateCreditCardNumber(ccNum) {
    const digits = cardDigits(ccNum);
    if (digits.length < 13 || digits.length > 19) return false;
    let sum = 0;
    let alt = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = parseInt(digits.charAt(i), 10);
      if (alt) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      alt = !alt;
    }
    return sum % 10 === 0;
  }

  const {dispatchData, loading, error} = useAcceptJs({
    environment:
      process.env.REACT_APP_ENV == "production" ? "PRODUCTION" : "SANDBOX",
    authData,
  });

  const initialValues = {
    nameOnCard: '',
    cardNumber: '',
    expiration: '',
    securityCode: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    amount: '',
    signed: false
  }

  const validate = (values) => {
    const errors = {};
    if (!values.nameOnCard) errors.nameOnCard = 'Name is required';
    if (!values.cardNumber) {
      errors.cardNumber = 'Card is required'
    } else if (!ValidateCreditCardNumber(values.cardNumber)) {
      errors.cardNumber = "Invalid CC Num";
    }
    if (!values.securityCode) {
      errors.securityCode = "required";
    }
    const expVal = String(values.expiration || "").trim();
    if (!expVal) {
      errors.expiration = "required";
    } else if (!/^\d{2}\/\d{2}$/.test(expVal)) {
      errors.expiration = "Use MM/YY format";
    } else {
      const [mm, yy] = expVal.split("/");
      const expMonth = parseInt(mm, 10);
      const expYear = 2000 + parseInt(yy, 10);
      const now = new Date();
      if (expMonth < 1 || expMonth > 12) {
        errors.expiration = "Invalid month";
      } else if (expYear < now.getFullYear() || (expYear === now.getFullYear() && expMonth < now.getMonth() + 1)) {
        errors.expiration = "Card is expired";
      }
    }
    if (!values.address1) {
      errors.address1 = "required";
    }
    if (!values.city) {
      errors.city = "required";
    }
    if (!values.state) {
      errors.state = "required";
    }
    if (!values.zip) {
      errors.zip = "required";
    }
    if (!values.signed) {
      errors.signed = "You must sign form by checking box.";
    }
    return errors;
  }

  const handleSubmit = async (values) => {
    if (submitting) return;

    if (loading) {
      openNotificationWithIcon(
        "error",
        "Payment",
        "Payment system is still initializing. Please wait a moment and try again.",
        "bottomRight",
      );
      return;
    }

    if (error) {
      openNotificationWithIcon(
        "error",
        "Payment",
        "Payment system error. Please refresh the page and try again.",
        "bottomRight",
      );
      return;
    }

    setSubmitting(true);
    let entryId = null;
    let riderId = null;
    let paymentApproved = false;
    let paymentDeclined = false;
    let paymentAttempted = false;

    try {
      if (createdRiderId) {
        riderId = createdRiderId;
      } else if (riderObject?.id) {
        riderId = riderObject.id;
      } else if (riderInputName && riderInputName.trim()) {
        // Resolve the typed rider name: reuse an existing rider if one already
        // matches, otherwise create a new one. This prevents both lost rider
        // names and duplicate rider records.
        const riderName = riderInputName.trim().toUpperCase();
        const existingRider = await getRiderByName(app, riderName);
        if (existingRider?._id) {
          riderId = existingRider._id.toString();
        } else {
          const newRiderResult = await createRider(app, { name: riderName });
          riderId = newRiderResult.insertedId.toString();
        }
        setCreatedRiderId(riderId);
      }

      if (createdEntryId) {
        entryId = createdEntryId;
        await updateEntry(app, entryId, {
          email: formData?.email ? formData?.email.toUpperCase() : "",
          draw: formData?.draw ? formData?.draw.toUpperCase() : "",
          rider: riderId ? { $oid: riderId } : null,
          paymentError: false,
        });
      } else if (existingEntry && (!existingEntry.paid || existingEntry.paymentError)) {
        entryId = existingEntry._id;
        setCreatedEntryId(entryId);
        await updateEntry(app, entryId, {
          email: formData?.email ? formData?.email.toUpperCase() : "",
          draw: formData?.draw ? formData?.draw.toUpperCase() : "",
          rider: riderId ? { $oid: riderId } : null,
          paymentError: false,
        });
      } else {
        // Create new entry with pending payment status
        const pendingEntryPayload = {
          foal: foalObject?._id,
          event: eventObject?._id,
          owner:  foalObject?.owner,
          email: formData?.email ? formData?.email.toUpperCase() : "",
          draw: formData?.draw ? formData?.draw.toUpperCase() : "",
          rider: riderId ? { $oid: riderId } : null,
          createdAt: new Date(),
          placement: null,
          time: null,
          paid: false // Start with pending payment status
        };
        
        const entryResult = await createEntry(app, pendingEntryPayload);
        entryId = entryResult.insertedId;
        setCreatedEntryId(entryId);
      }

      const exp = String(values.expiration).trim();
      const [expMonth, expYear] = exp.split("/");
      const ccDigits = cardDigits(values.cardNumber);
      let cardData = {
        cardNumber: ccDigits,
        month: expMonth,
        year: `20${expYear}`,
        cardCode: String(values.securityCode),
      };
      
      if (!dispatchData) {
        throw new Error('Payment system not ready. Please refresh the page and try again.');
      }
      
      let response = await dispatchData({cardData});
      let paymentRequest = createPaymentRequest(
        response,
        values,
        entryId,
      );

      paymentAttempted = true;
      let payment = await axios.post(
        `${process.env.REACT_APP_MANAGEMENT_API_URL}/payment`,
        paymentRequest,
      );
      if (isPaymentApproved(payment.data)) {
        paymentApproved = true;
        const paymentInfo = {
          amountPaid: paymentRequest.amount,
          transactionId: payment.data.transactionId,
          paymentCreated: new Date(),
          last4CC: ccDigits.slice(-4),
          paid: true,
          paymentError: false
        }

        // If backend DB save failed, try fallback PUT to the entry payment endpoint.
        if (!payment.data.dbSaved) {
          try {
            await axios.put(
              `${process.env.REACT_APP_MANAGEMENT_API_URL}/entry/${entryId}/payment`,
              { ...paymentInfo }
            );
          } catch (fallbackError) {
            console.error('Fallback payment save also failed:', fallbackError);
            try {
              await updateEntry(app, entryId, {
                paymentError: true,
                transactionId: payment.data.transactionId
              });
            } catch (realmError) {
              console.log('updateEntry failed:', realmError);
            }
          }
        }

        await completeFormSubmission(paymentInfo);
      } else {
        // API returned 200 but payment was explicitly not approved (e.g. declined)
        paymentDeclined = true;
        throw new Error('Payment was not approved');
      }

      // openNotificationWithIcon(
      //   "success",
      //   "Payment",
      //   "The payment has been accepted and your entry form submitted.",
      //   "bottomRight",
      // );
    } catch (e) {
      if (entryId && paymentDeclined && !existingEntry) {
        try {
          await axios.delete(
            `${process.env.REACT_APP_MANAGEMENT_API_URL}/entry/${entryId}`
          );
        } catch (cleanupError) {
          console.error('Failed to cleanup pending entry:', cleanupError);
        }
      }

      if (!paymentAttempted && entryId) {
        try {
          await updateEntry(app, entryId, { paymentError: true });
        } catch (flagError) {
          console.error('Failed to flag entry after tokenization failure:', flagError);
        }
      } else if (paymentAttempted && !paymentApproved && !paymentDeclined) {
        try {
          if (!entryId) {
            const entryResult = await createEntry(app, {
              foal: foalObject?._id,
              event: eventObject?._id,
              owner: foalObject?.owner,
              email: formData?.email ? formData?.email.toUpperCase() : "",
              draw: formData?.draw ? formData?.draw.toUpperCase() : "",
              rider: riderId ? { $oid: riderId } : null,
              createdAt: new Date(),
              placement: null,
              time: null,
              paid: false,
              paymentError: true
            });
            entryId = entryResult.insertedId;
            setCreatedEntryId(entryId);
          } else {
            await updateEntry(app, entryId, { paymentError: true });
          }
        } catch (flagError) {
          console.error('Failed to save paymentError:', flagError);
        }
      }

      if (paymentApproved) {
        openNotificationWithIcon(
          "success",
          "Payment",
          "Your payment was successful and your entry has been submitted. If you did not receive a confirmation email, please contact support.",
          "bottomRight",
        );
      } else if (paymentAttempted && !paymentDeclined && entryId) {
        openNotificationWithIcon(
          "warning",
          "Payment Not Confirmed",
          "Your payment could not be confirmed. Your entry has been saved. Please try again or contact support.",
          "bottomRight",
        );
      } else {
        let message = "Error on Payment";
        if (e?.messages?.message?.length > 0) {
          message = e.messages.message[0].text;
        }
        if (e?.response?.data?.message) {
          message = e.response.data.message;
        }
        openNotificationWithIcon(
          "error",
          "Payment",
          message + ". Please try again.",
          "bottomRight",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  function createPaymentRequest(response, values, entryId) {
    const ccDigits = cardDigits(values.cardNumber);

    const payload = {
      amount: process.env.REACT_APP_ENV === "production" ? String(amountDue) : "1.00",
      setDataDescriptor: response.opaqueData.dataDescriptor,
      dataValue: response.opaqueData.dataValue,
      paymentType: "entry",
      eventName: eventObject?.name || "Unknown Event",
      entryId: entryId ? String(entryId) : null,
      last4CC: ccDigits.slice(-4),
      customer: {
        firstName: values.nameOnCard.split(" ").slice(0, -1).join(" "),
        lastName: values.nameOnCard.split(" ").slice(-1).join(" ") || "",
        foal: foalObject?.name || horseName,
        address: `${values.address1} ${values.address2}`,
        city: values.city || "",
        state: values.state || "",
        zip: values.zip || "",
      },
    };

    return payload
  }


  return (
    <>
      {contextHolder}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ values, setFieldValue }) => (
          <Card title="Please provide payment details."
                bordered={false}>
            <Form layout="vertical">
              {existingEntry?.paymentError && (
                <Alert
                  message="Previous Payment Attempt Not Confirmed"
                  description="A payment was previously attempted but could not be confirmed. Please check your bank or credit card statement. If you were NOT charged, you may proceed to pay below. If you WERE charged, do not pay again — contact TLM at enrollments@tlmamas.com for assistance."
                  type="warning"
                  showIcon
                  style={{ marginBottom: "1rem" }}
                />
              )}
              <Row gutter={[32, 32]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Name on Card"
                    name="nameOnCard"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input name="nameOnCard" placeholder="Name"/>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Card Number"
                    name="cardNumber"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input
                      name="cardNumber"
                      placeholder="Card Number"
                      maxLength={23}
                      inputMode="numeric"
                      autoComplete="cc-number"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Expiration"
                    name="expiration"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input
                      maxLength={5}
                      name="expiration"
                      placeholder="MM/YY"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      onChange={expirationFieldOnChange(setFieldValue)}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[32, 32]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Security Code"
                    name="securityCode"
                    hasFeedback={true}
                    type="number"
                    showValidateSuccess={true}
                  >
                    <Input
                      controls={false}
                      name="securityCode"
                      placeholder="Code"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Address Line 1"
                    name="address1"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input name="address1" placeholder="Address Line 1"/>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Address Line 2"
                    name="address2"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input name="address2" placeholder="Address Line 2"/>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[32, 32]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="City"
                    name="city"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input name="city" placeholder="City"/>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="State"
                    name="state"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input name="state" placeholder="State"/>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Zip"
                    name="zip"
                    hasFeedback={true}
                    showValidateSuccess={true}
                  >
                    <Input name="zip" placeholder="Zip"/>
                    <p>
                      <small>
                        *Foreign Addresses please enter your postal code without
                        spaces. This cannot be left blank.
                      </small>
                    </p>
                  </Form.Item>
                </Col>
              </Row>


              <Form.Item
                label=""
                name="amount"
                hasFeedback={true}
                showValidateSuccess={true}
              >
                <Row gutter={[32, 32]}>
                  <Col offset={0}>
                    <Statistic
                      title="Amount Due Today: "
                      value={`$${amountDue}`}
                    />
                  </Col>
                </Row>
              </Form.Item>
              <EntryPaymentDisclosure/>
              <Form.Item
                label="Sign by checking:"
                name="signed"
                hasFeedback={true}
              >
                <Checkbox
                  type="checkbox"
                  name="signed"
                />
              </Form.Item>
              <Form.Item label=""
                         name="cancel">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 5,
                    marginTop: '20px'
                  }}
                >
                  <Button
                    size="large"
                    onClick={()=>window.location.reload()}
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    size="large"
                    loading={loading || submitting}
                    disabled={loading || submitting || error}
                  >
                    {submitting ? "Processing Payment..." : loading ? "Loading..." : "Submit Payment"}
                  </SubmitButton>
                </div>
              </Form.Item>

            </Form>
          </Card>
        )}
      </Formik>
    </>
  );
};

export default EntryPaymentForm;